import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Card, PageTitle, Property, PropertyList } from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { BackLink } from '../components/Layout.js';
import { BlockSkeleton, ErrorAlert, SuccessAlert } from '../components/ui.js';

interface PartnerDetail {
  id: string;
  companyName: string;
  email: string;
  phone: string | null;
  registrationNumber: string;
  partnerType: string | null;
  approvalStatus: string;
  declineComment: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  profile: {
    firstName: string;
    lastName: string;
    contactEmail: string;
    alternatePhone: string | null;
    streetNumber: string;
    postalCode: string;
    address: string;
    latitude: number | null;
    longitude: number | null;
    matchRadiusKm: number;
  } | null;
  documents: Array<{ id: string; kind: string; name: string; url: string; mimeType: string }>;
  decisions: Array<{
    decision: string;
    comment: string | null;
    createdAt: string;
    admin: { id: string; name: string; username: string };
  }>;
  stats: { offers: number; leads: number; reviews: number };
}

/**
 * Replaces viewinsurance_comp.php plus acceptApplicant.php / declineApplicant.php.
 *
 * The decision history is new: the legacy console overwrote `approval` with no
 * record of who changed it or why, so a disputed rejection was unanswerable.
 */
export function PartnerDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [comment, setComment] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, error, isLoading } = useQuery({
    queryKey: ['partner', id],
    queryFn: () => api.get<PartnerDetail>(`/admin/partners/${id}`),
    enabled: Boolean(id),
  });

  const decide = useMutation({
    mutationFn: (decision: 'APPROVED' | 'DECLINED') =>
      api.post<{ status: string; backfilledLeads: number }>(`/admin/partners/${id}/decision`, {
        decision,
        comment: comment.trim() || undefined,
        documentAssetIds: [],
      }),
    onSuccess: (result) => {
      setComment('');
      setFeedback(
        result.status === 'APPROVED'
          ? t('admin.partnerDetail.approvedFeedback', { count: result.backfilledLeads })
          : t('admin.partnerDetail.declinedFeedback'),
      );
      void queryClient.invalidateQueries({ queryKey: ['partner', id] });
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-counts'] });
    },
  });

  if (isLoading) {
    return (
      <>
        <PageTitle heading={t('admin.nav.partners')} icon="pe-7s-culture" />
        <BlockSkeleton height={220} />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageTitle heading={t('admin.nav.partners')} icon="pe-7s-culture" />
        <ErrorAlert error={error} />
      </>
    );
  }

  const canDecide = can('SUPER_ADMIN', 'ADMIN');

  return (
    <>
      <PageTitle
        heading={data.companyName}
        subheading={data.registrationNumber}
        icon="pe-7s-culture"
        actions={
          <>
            <Badge value={data.approvalStatus}>{fmt.approval(data.approvalStatus)}</Badge>
            <span className="ml-2">
              <BackLink to="/partners">{t('common.back')}</BackLink>
            </span>
          </>
        }
      />

      {feedback && <SuccessAlert>{feedback}</SuccessAlert>}
      <ErrorAlert error={decide.error} />

      <div className="row">
        <div className="col-lg-8">
          <Card title={<div className="card-header-title">{t('admin.partnerDetail.masterData')}</div>}>
            <PropertyList>
              <tbody>
                <Property label={t('fields.company')}>{data.companyName}</Property>
                <Property label={t('fields.contactPerson')}>
                  {data.profile ? `${data.profile.firstName} ${data.profile.lastName}` : fmt.empty}
                </Property>
                <Property label={t('fields.email')}>{data.email}</Property>
                <Property label={t('fields.phone')}>{data.phone ?? fmt.empty}</Property>
                <Property label={t('fields.address')}>{data.profile?.address ?? fmt.empty}</Property>
                <Property label={t('fields.postalCode')}>
                  {data.profile?.postalCode ?? fmt.empty}
                </Property>
                <Property label={t('fields.radius')}>
                  {data.profile
                    ? t('common.kilometres', { km: data.profile.matchRadiusKm })
                    : fmt.empty}
                </Property>
                <Property label={t('fields.coordinates')}>
                  {data.profile?.latitude != null && data.profile?.longitude != null
                    ? `${data.profile.latitude.toFixed(5)}, ${data.profile.longitude.toFixed(5)}`
                    : t('admin.partnerDetail.coordinatesMissing')}
                </Property>
                <Property label={t('fields.registeredAt')}>{fmt.dateTime(data.createdAt)}</Property>
                <Property label={t('fields.lastSignIn')}>{fmt.dateTime(data.lastLoginAt)}</Property>
              </tbody>
            </PropertyList>

            {data.profile?.latitude == null && (
              <div className="mt-3">
                <Alert tone="warning">
                  {t('admin.partnerDetail.noCoordinatesWarning')}
                </Alert>
              </div>
            )}
          </Card>

          <Card title={<div className="card-header-title">{t('admin.partnerDetail.documents')}</div>}>
            {data.documents.length === 0 ? (
              <p className="text-muted mb-0">{t('admin.partnerDetail.noDocuments')}</p>
            ) : (
              <ul className="mb-0 pl-3">
                {data.documents.map((document) => (
                  <li key={document.id}>
                    <a href={document.url} target="_blank" rel="noreferrer noopener">
                      {document.name}
                    </a>{' '}
                    <span className="small text-muted">
                      (
                      {document.kind === 'REGISTRATION'
                        ? t('admin.partnerDetail.documentRegistration')
                        : t('admin.partnerDetail.documentApproval')}
                      )
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title={
              <div className="card-header-title">{t('admin.partnerDetail.decisionHistory')}</div>
            }
            noPadding
          >
            {data.decisions.length === 0 ? (
              <div className="p-3 text-muted">{t('admin.partnerDetail.noDecision')}</div>
            ) : (
              <table className="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>{t('common.date')}</th>
                    <th>{t('admin.partnerDetail.decision')}</th>
                    <th>{t('admin.partnerDetail.handledBy')}</th>
                    <th>{t('common.reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.decisions.map((decision, index) => (
                    <tr key={`${decision.createdAt}-${index}`}>
                      <td>{fmt.dateTime(decision.createdAt)}</td>
                      <td>
                        <Badge value={decision.decision}>{fmt.approval(decision.decision)}</Badge>
                      </td>
                      <td>{decision.admin.name}</td>
                      <td>{decision.comment ?? fmt.empty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div className="col-lg-4">
          <Card title={<div className="card-header-title">{t('admin.partnerDetail.activity')}</div>}>
            <PropertyList>
              <tbody>
                <Property label={t('admin.partnerDetail.assignedLeads')}>{data.stats.leads}</Property>
                <Property label={t('admin.partnerDetail.sentOffers')}>{data.stats.offers}</Property>
                <Property label={t('fields.reviews')}>{data.stats.reviews}</Property>
              </tbody>
            </PropertyList>
          </Card>

          {canDecide && (
            <Card title={<div className="card-header-title">{t('admin.partnerDetail.decision')}</div>}>
              <div className="position-relative form-group">
                <label htmlFor="comment">{t('common.reason')}</label>
                <textarea
                  id="comment"
                  className="form-control"
                  rows={4}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder={t('admin.partnerDetail.reasonPlaceholder')}
                />
                <small className="form-text text-muted">
                  {t('admin.partnerDetail.reasonHint')}
                </small>
              </div>

              <button
                type="button"
                className="btn btn-success mr-2"
                disabled={decide.isPending || data.approvalStatus === 'APPROVED'}
                onClick={() => decide.mutate('APPROVED')}
              >
                {t('common.approve')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={
                  decide.isPending ||
                  data.approvalStatus === 'DECLINED' ||
                  comment.trim().length === 0
                }
                onClick={() => decide.mutate('DECLINED')}
              >
                {t('common.decline')}
              </button>

              {data.approvalStatus === 'PENDING' && (
                <p className="small text-muted mt-3 mb-0">
                  {t('admin.partnerDetail.approvedNote')}
                </p>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
